import { Button, Grid, Link, TextField, Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { T, useT } from 'react-polyglot-hooks';
import { Link as RouterLink } from 'react-router-dom';
import useReactRouter from 'use-react-router';
import validate from 'validate.js';
import { LoadingOverlay } from '../../../components/LoadingOverlay';
import { cleanCellphone, InternationalPhoneInput } from '../../../components/PhoneInput/PhoneInput';
import anyLoading from '../../../utils/anyLoading';
import forgotPassword from './forgotPassword.api';
import forgotPasswordSms from './forgotPasswordSms.api';
import updateForgottenPasswordSms from './updateForgottenPasswordSms.api';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        backgroundColor: theme.palette.background.default,
        height: '100%',
    },
    grid: {
        height: '100%'
    },
    content: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    },

    contentHeader: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: theme.spacing(5),
        paddingBototm: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2)
    },
    contentBody: {
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
            justifyContent: 'center'
        }
    },
    form: {
        paddingLeft: 100,
        paddingRight: 100,
        paddingBottom: 125,
        [theme.breakpoints.up('md')]: {
          flexBasis: 700,  
        },
        [theme.breakpoints.down('sm')]: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2)
        }
    },
    title: {
        marginTop: theme.spacing(3)
    },
    textField: {
        marginTop: theme.spacing(2)
    },
    forgotButton: {
        margin: theme.spacing(2, 0)
    }
}));

export const ForgotPassword = () => {
    const [sendForgotPassword, forgotPasswordData] = forgotPassword();
    const [sendForgotPasswordSms, forgotPasswordSmsData] = forgotPasswordSms();
    const [sendUpdateForgottenPasswordSms, updateForgottenPasswordSmsData] = updateForgottenPasswordSms();
    const loadingData = anyLoading(forgotPasswordData, forgotPasswordSmsData, updateForgottenPasswordSmsData);
    const classes = useStyles();
    const {history} = useReactRouter();
    const {enqueueSnackbar} = useSnackbar();
    const t = useT();
    const [sms, setSms] = useState(false);
    const [smsPassword, setSmsPassword] = useState(false);
    const [smsDisableInput, setSmsDisableInput] = useState(false);

    const emailSchema = {
        email: {
            presence: {allowEmpty: false, message: t('is required')},
            email: true,
            length: {maximum: 64}
        }
    };

    const smsSchema = {
        cellphone: {
            presence: {allowEmpty: false, message: t('is required')},
            phone: true,
        }
    };

    const smsPasswordSchema = {
        code: {
            presence: {allowEmpty: false, message: t('is required')},
            length: {is: 6}
        },
        password: {
            password: true,
        },
        password_confirmation: {
            presence: {allowEmpty: false, message: t('is required')},
            equality: {
                attribute: 'password',
                message: t('Must match your password'),
            }
        },
    };

    const [formState, setFormState] = useState({
        isValid: false,
        values: {
            email: '',
            cellphone: '',
            cellphone_code: '',
            code: '',
            password: '',
            password_confirmation: '',
        },
        touched: {
            email: null,
            cellphone: null,
            cellphone_code: null,
            code: null,
            password: null,
            password_confirmation: null,
        },
        errors: {
            email: null,
            cellphone: null,
            cellphone_code: null,
            code: null,
            password: null,
            password_confirmation: null,
        }
    });

    const handleChange = (event: any) => {
        event.persist();

        setFormState(state => ({
            ...state,
            values: {
                ...state.values,
                [event.target.name]: event.target.value
            },
            touched: {
                ...state.touched,
                [event.target.name]: true
            }
        }));
    };

    useEffect(() => {
        const errors = validate(formState.values, smsPassword? smsPasswordSchema : sms? smsSchema : emailSchema);

        setFormState(state => ({
            ...state,
            isValid: !errors,
            errors: errors || {}
        }));
    }, [formState.values, sms, smsPassword]);

    const hasError = (field: string) => {
        // @ts-ignore
        return !!(formState.touched[field] && formState.errors[field]);
    };

    const onFormSubmit = (event: any) => {
        event.preventDefault();

        if(smsPassword){
            sendUpdateForgottenPasswordSms({variables: {data: {
                        cellphone: cleanCellphone(formState.values.cellphone),
                        cellphone_code: formState.values.cellphone_code,
                        code: formState.values.code,
                        password: formState.values.password,
                        password_confirmation: formState.values.password_confirmation,
                    }}})
              .then((response) => {
                  const status = response.data!.updateForgottenPasswordSms!.status;
                  let message = '';

                  switch (status) {
                      case 'PASSWORD_NOT_UPDATED':
                          message = t('Your password could not be updated.').toString();
                          enqueueSnackbar(message, {variant: 'error'});
                          break;
                      case 'PASSWORD_UPDATED':
                          message = t('Your password has been updated.').toString();
                          enqueueSnackbar(message, {variant: 'success'});
                          break;
                      default:
                          console.log(response);
                          break;
                  }

                  history.push(`/login`);
              })
              .catch(() => {
                  enqueueSnackbar(t('There was an error communicating with the server.'), {variant: 'error'});
              });
        } else if(sms) {

            setSmsDisableInput(true);

            sendForgotPasswordSms({variables: {data: {
                cellphone: cleanCellphone(formState.values.cellphone),
                cellphone_code: formState.values.cellphone_code,
                    }}})
              .then((response) => {
                  const status = response.data!.forgotPasswordSms!.status;
                  let message = '';

                  switch (status) {
                      case 'SMS_NOT_SENT':
                          message = t('We could not find your cellphone. Please check and try again.').toString();
                          enqueueSnackbar(message, {variant: 'error'});
                          break;
                      case 'SMS_SENT':
                          message = t('You have been sent an sms with instructions. Please check your phone.').toString();
                          enqueueSnackbar(message, {variant: 'success'});
                          break;
                      default:
                          console.log(response);
                          break;
                  }

                  setSmsPassword(true);
              })
              .catch(() => {
                  enqueueSnackbar(t('There was an error communicating with the server.'), {variant: 'error'});
              });
        } else {
            sendForgotPassword({variables: {data: {email: formState.values.email}}})
              .then((response) => {
                  const status = response.data!.forgotPassword!.status;
                  let message = '';

                  switch (status) {
                      case 'EMAIL_NOT_SENT':
                          message = t('We could not find your email address. Please check and try again.').toString();
                          enqueueSnackbar(message, {variant: 'error'});
                          break;
                      case 'EMAIL_SENT':
                          message = t('You have been sent an email with instructions. Please check your inbox.').toString();
                          enqueueSnackbar(message, {variant: 'success'});
                          break;
                      default:
                          console.log(response);
                          break;
                  }

                  history.push(`/login`);
              })
              .catch(() => {
                  enqueueSnackbar(t('There was an error communicating with the server.'), {variant: 'error'});
              });
        }
    };

    return (
        <LoadingOverlay loading={loadingData.loading}>
            <Grid item className={classes.content} lg={12} xs={12}>
                <div className={classes.content}>
                    <div className={classes.contentHeader}/>
                    <div className={classes.contentBody}>
                        <form className={classes.form} onSubmit={onFormSubmit}>
                            <Typography className={classes.title} variant="h2"><T phrase="Forgot Password"/></Typography>

                            {forgotPasswordData.error && (
                                <Typography color="error" className={classes.title} variant="h4">
                                    {forgotPasswordData.error.message}
                                </Typography>
                            )}

                            {!smsPassword && !sms && (
                              <TextField
                                className={classes.textField}
                                error={hasError('email')}
                                fullWidth
                                helperText={
                                    hasError('email') ? formState.errors.email![0] : null
                                }
                                label={t('Email address')}
                                name="email"
                                onChange={handleChange}
                                type="text"
                                value={formState.values!.email! || ''}
                                variant="outlined"
                              />
                            )}

                            {sms && (
                              <InternationalPhoneInput
                                className={classes.textField}
                                error={hasError('cellphone')}
                                fullWidth
                                helperText={
                                    hasError('cellphone') ? formState.errors.cellphone![0] : null
                                }
                                notched="true"
                                label={t('Cellphone')}
                                name="cellphone"
                                onChange={handleChange}
                                onCodeChange={handleChange}
                                type="text"
                                value={formState.values!.cellphone! || ''}
                                codeValue={formState.values!.cellphone_code! || '+1'}
                                variant="outlined"
                                disabled={smsDisableInput}
                              />
                            )}

                            {smsPassword && (
                              <React.Fragment>
                                  <TextField
                                    className={classes.textField}
                                    error={hasError('code')}
                                    fullWidth
                                    helperText={
                                        hasError('code') ? formState.errors.code![0] : null
                                    }
                                    label={t('Code')}
                                    name="code"
                                    onChange={handleChange}
                                    type="text"
                                    value={formState.values!.code! || ''}
                                    variant="outlined"
                                  />

                                  <TextField
                                    className={classes.textField}
                                    error={hasError('password')}
                                    fullWidth
                                    helperText={
                                        hasError('password') ? formState.errors.password![0] : null
                                    }
                                    label={t('Password')}
                                    name="password"
                                    onChange={handleChange}
                                    type="password"
                                    value={formState.values.password || ''}
                                    variant="outlined"
                                  />

                                <TextField
                                    className={classes.textField}
                                    error={hasError('password_confirmation')}
                                    fullWidth
                                    helperText={
                                        hasError('password_confirmation') ? formState.errors.password_confirmation![0] : null
                                    }
                                    label={t('Confirm Password')}
                                    name="password_confirmation"
                                    onChange={handleChange}
                                    type="password"
                                    value={formState.values.password_confirmation || ''}
                                    variant="outlined"
                                />
                              </React.Fragment>
                            )}

                            <Button
                                className={classes.forgotButton}
                                color="primary"
                                disabled={!formState.isValid}
                                fullWidth
                                size="large"
                                type="submit"
                                variant="contained"
                            >
                                <T phrase="Submit"/>
                            </Button>
                            <Typography color="textSecondary" variant="body1">
                                <T phrase="Remembered your password?"/>&nbsp;
                                <Link color="textPrimary" variant="h6" component={RouterLink} to="/"><T phrase="Log In" /></Link>
                            </Typography>
                            {!smsPassword && (
                              <Typography color="textSecondary" variant="body1">
                                  <T phrase="Reset password by "/>&nbsp;
                                  <Link color="textPrimary" variant="h6" onClick={() => setSms(!sms)}><T phrase={sms? 'Email' : 'Cellphone'} /></Link>
                              </Typography>
                            )}
                            {sms && !smsPassword && (
                              <Typography color="textSecondary" variant="body1">
                                  <T phrase="Already have a code? "/>&nbsp;
                                  <Link color="textPrimary" variant="h6" onClick={() => setSmsPassword(true)}><T
                                    phrase="Enter code"/></Link>
                              </Typography>
                            )}
                        </form>
                    </div>
                </div>
            </Grid>

        </LoadingOverlay>
    );
};

export default ForgotPassword;
